import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MaterialModule } from '../../../material.module';
import { CommonModule } from '@angular/common';

interface TocItem {
  id: string;
  label: string;
}

interface Permission {
  name: string;
  when: string;
  purpose: string;
}

interface RetentionRow {
  category: string;
  period: string;
  rationale: string;
}

interface SdkRow {
  name: string;
  purpose: string;
  data: string;
  policy: string;
}

interface Right {
  title: string;
  description: string;
  how: string;
}


@Component({
  selector: 'app-privacy-policy',
  standalone: true,
  imports: [RouterModule, MaterialModule, CommonModule],
  templateUrl: './privacy-policy.component.html',
  styleUrl: './privacy-policy.component.scss'
})
export class PrivacyPolicyComponent  implements OnInit, OnDestroy {
  activeSectionId = 'disclosure';
  private observer!: IntersectionObserver;

  tocItems: TocItem[] = [
    { id: 'disclosure', label: 'Prominent Disclosure' },
    { id: 's1',  label: '1. Introduction & Scope' },
    { id: 's2',  label: '2. Data We Collect' },
    { id: 's3',  label: '3. How We Use Your Data' },
    { id: 's4',  label: '4. Data Sharing' },
    { id: 's5',  label: '5. Consent & Controls' },
    { id: 's6',  label: '6. Retention & Deletion' },
    { id: 's7',  label: '7. Data Security' },
    { id: 's8',  label: '8. Third-Party SDKs' },
    { id: 's9',  label: '9. Your Rights' },
    { id: 's10', label: '10. Legal Bases' },
    { id: 's11', label: '11. Cross-Border Transfers' },
    { id: 's12', label: '12. Children\'s Privacy' },
    { id: 's13', label: '13. Cookies' },
    { id: 's14', label: '14. App Set ID' },
    { id: 's15', label: '15. Policy Updates' },
    { id: 's16', label: '16. Contact Us' },
    { id: 's17', label: '17. Definitions' },
  ];

  purposes = [
    { purpose: 'Service matching and dispatch',     basis: 'Contract',                        data: 'Location, vehicle, account' },
    { purpose: 'Real-time service tracking',        basis: 'Contract / Consent',               data: 'GPS location' },
    { purpose: 'Payment processing and billing',    basis: 'Contract',                        data: 'Payment, service records' },
    { purpose: 'Identity & provider verification',  basis: 'Legal obligation / Contract',      data: 'ID docs, selfies, certifications' },
    { purpose: 'Fraud prevention & security',       basis: 'Legitimate interests',             data: 'Device IDs, behaviour patterns' },
    { purpose: 'Customer support',                  basis: 'Contract / Legitimate interests',  data: 'Communications, service records' },
    { purpose: 'Service quality monitoring',        basis: 'Legitimate interests',             data: 'Ratings, performance data' },
    { purpose: 'Regulatory & tax compliance',       basis: 'Legal obligation',                 data: 'Financial transactions, KYC data' },
    { purpose: 'Marketing communications',          basis: 'Consent (opt-in only)',            data: 'Email, phone, preferences' },
    { purpose: 'App improvement & analytics',       basis: 'Legitimate interests',             data: 'Anonymised usage data' },
    { purpose: 'Emergency coordination',            basis: 'Vital interests / Consent',        data: 'Location, emergency contacts' },
  ];

  permissions: Permission[] = [
    { name: 'Precise Location (foreground)', when: 'On first service request',      purpose: 'Match providers, show map' },
    { name: 'Precise Location (background)', when: 'When service is active',        purpose: 'Real-time tracking during service' },
    { name: 'Camera',                        when: 'When uploading vehicle photo',  purpose: 'Vehicle / issue documentation' },
    { name: 'Notifications',                 when: 'On first app launch',           purpose: 'Service updates and alerts' },
    { name: 'Contacts (optional)',           when: 'When adding emergency contact', purpose: 'Emergency notification only' },
  ];

  retentionRows: RetentionRow[] = [
    { category: 'Account data',               period: 'Life of account + 7 years post-deletion', rationale: 'Legal / regulatory' },
    { category: 'Service records',            period: '7 years from service completion',          rationale: 'Warranty, legal' },
    { category: 'Financial transactions',     period: '7 years',                                  rationale: 'Kenyan tax law' },
    { category: 'Precise location data',      period: '2 years, then anonymised',                 rationale: 'Service optimisation' },
    { category: 'Vehicle service history',    period: '5 years',                                  rationale: 'Maintenance tracking' },
    { category: 'Identity verification docs', period: 'Duration of account + 2 years',            rationale: 'Fraud prevention' },
    { category: 'Chat / communication logs',  period: '3 years',                                  rationale: 'Dispute resolution' },
    { category: 'Marketing consent records',  period: 'Until consent withdrawn + 5 years',        rationale: 'Legal compliance' },
  ];

  sdkRows: SdkRow[] = [
    { name: 'Google Maps SDK',          purpose: 'Maps and routing',          data: 'Location',                       policy: 'policies.google.com/privacy' },
    { name: 'Firebase (Google)',        purpose: 'Analytics, crash reporting', data: 'Device ID, usage (anonymised)', policy: 'firebase.google.com/support/privacy' },
    { name: 'Safaricom M-Pesa API',     purpose: 'Payment processing',        data: 'Transaction ref, amount',       policy: 'safaricom.co.ke/privacy' },
    { name: 'Twilio / Africa\'s Talking', purpose: 'SMS / notifications',     data: 'Phone number',                  policy: 'twilio.com/legal/privacy' },
    { name: 'Sentry',                   purpose: 'Error monitoring',          data: 'Device info, stack traces',     policy: 'sentry.io/privacy' },
  ];

  rights: Right[] = [
    { title: 'To Be Informed',   description: 'Know what data we collect and why',                how: 'This policy; in-app disclosures' },
    { title: 'To Access',        description: 'Obtain a copy of your personal data',              how: 'Settings → Privacy Center → Download Data' },
    { title: 'To Correction',    description: 'Fix inaccurate or misleading data',               how: 'Settings → Edit Profile or contact DPO' },
    { title: 'To Deletion',      description: 'Erase data (subject to legal retention)',         how: 'Settings → Delete Account or contact DPO' },
    { title: 'To Object',        description: 'Object to specific processing activities',        how: 'dpo@autogizmo.com' },
    { title: 'To Portability',   description: 'Receive your data in a portable format',          how: 'Settings → Privacy Center → Export Data' },
    { title: 'To Human Review',  description: 'Review of automated decisions affecting you',     how: 'Contact DPO with specific request' },
  ];

  definitions = [
    { term: 'Customer / Driver',   def: 'An individual requesting automotive services through AutoGizmo.' },
    { term: 'Service Provider',    def: 'A professional or company fulfilling service requests via AutoGizmo.' },
    { term: 'Personal Data',       def: 'Any information relating to an identified or identifiable natural person (DPA, 2019).' },
    { term: 'Processing',          def: 'Any operation performed on personal data (Section 2, DPA, 2019).' },
    { term: 'Data Controller',     def: 'Galweather Consulting Limited (trading as AutoGizmo), which determines the purposes and means of processing.' },
    { term: 'ODPC',                def: 'Office of the Data Protection Commissioner, Kenya.' },
    { term: 'App Set ID',          def: 'An Android identifier scoped to a set of apps, resettable by the user, used only for analytics and fraud prevention.' },
  ];

  scrollTo(id: string): void {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  ngOnInit(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) this.activeSectionId = e.target.id;
        });
      },
      { threshold: 0.25 }
    );
    const ids = this.tocItems.map((t) => t.id);
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) this.observer.observe(el);
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}

